import ReactDOM from 'react-dom';
import BigCardView, {BigCardOutline} from '../../components/elem-play/BigCardView';
import { ElemCard, bigCardWidth, bigCardHeight } from './ElemHomeView';

export class BigCard {
    constructor( outline=null, htmlRoot=null ) {
        this.outline = outline; // svg for visible outline of big card, or null
        this.htmlRoot = htmlRoot;
        this.caseIndices = []; // case indices of cards dropped (and visible in Big card)
    }

    static border = 50;
    static corner = 16;
    static width = bigCardWidth;
    static height = bigCardHeight;

    /** is the Big card visible (not hidden) */
    cardIsVisible( Dyn ) {
        return Dyn.plotUtil.isElementVisible( this.outline );
    }
    /** is the given case in the big card (dropped and not yet removed/reset) */
    hasCaseIndex( caseIndex ) {
        return this.caseIndices.includes( caseIndex );
    }
    /** does the card have any cases visible? */
    cardHasCases() {
        return this.caseIndices.length > 0;
    }
    addCase( caseIndex ) {
        this.caseIndices.push( caseIndex );
    }
    removeCase() {
        return this.caseIndices.shift();
    }
}

export class ElemBigView {

    constructor( Dyn, container ) {

        // references to other objects
        this.Dyn = Dyn;
        this.container = container;

        // our stuff
        this.bounds = new Dyn.mathUtil.Rect(0,0,0,0); // area where big cards are allowed
        this.bigCards = [ new BigCard(), new BigCard() ];
    }

    destroy() {
        this.Dyn = null;
        this.container = null;
    }

    initVisuals() {
        //let Dyn = this.Dyn;

        // this.topMargin.height = Dyn.plotUtil.kPlotTitleFontSize * 2;
        // this.topMargin.title = this.container.paper.text( 0, 0, Dyn.loc(ElemPlayView.playTitle) )
        //     .attr({ 'font-size': Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize )});
        //
        // this.bottomMargin.height = Dyn.plotUtil.kAxisTitleFontSize * 2;
        // this.bottomMargin.title = this.container.paper.text( 0, 0, "" )
        //     .attr({ 'font-size': Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisTitleFontSize )})
        //     .hide();
    }

    updateVisuals() {
        let showCards = ! this.container.hasPlot();
        this.bigCards.forEach((bigCard, bigCardIndex) => this._updateOneCard( bigCard, bigCardIndex, showCards ));
    }

    _updateOneCard( bigCard, bigCardIndex, show=true ) {
        if( bigCard.cardHasCases() ) {
            let caseIndex = bigCard.caseIndices[bigCard.caseIndices.length - 1],
                smallCardIndex = this.container.home.caseIndexToSmallCardIndex( caseIndex );
            bigCard.htmlRoot.render( <BigCardView onClose={ () => this.removeCaseFromBigCard( bigCardIndex ) }
                                                  Dyn={ this.Dyn } caseIndex={ caseIndex } smallCardIndex={ smallCardIndex }/> );
        } else {
            bigCard.htmlRoot.render( <BigCardOutline Dyn={ this.Dyn }/> );
        }
        if( bigCard?.outline ) {
            this.Dyn.plotUtil.showOrHideElement( bigCard.outline, show );
        }
    }

    resize( newSize ) {
        let Dyn = this.Dyn;
        this.bounds.copyRect( newSize );
        this.bounds.addDebugRect( this.container.paper, "red" );
        Dyn.assertValidRect( this.bounds );
        this.arrangeBigCards();
    }

    /** arrange small cards in z-order, card backs over outlines */
    layerElements() {
    }

    /** assuming cards are not in a plot, are any cards dropped on the big card? */
    getBigCardState() {
        let casesOfEachCard = this.bigCards.map((bc) => [...bc.caseIndices]),
            bigCardCases = casesOfEachCard.flat();
        if( bigCardCases.length > 0 ) {
            return { plot:"bigCards", cases:casesOfEachCard };
        }
        return { plot: this.container.home.getHomeState() }
    }

    _getBigCardScale() {   // get scale of cards that fits in view
        let borderX = this.bounds.width / 10,
            borderY = this.bounds.height / 10,
            maxCardWidth1Across = (this.bounds.width - (2 * borderX)) / 1,
            maxCardWidth2Across = (this.bounds.width - (3 * borderX)) / 2,
            maxCardHeight1Down = (this.bounds.height - (2 * borderY)) / 1,
            maxCardHeight2Down = (this.bounds.height - (3 * borderX)) / 2,
            scale2Across = Math.min( maxCardWidth2Across / BigCard.width, maxCardHeight1Down / BigCard.height ),
            scale2Down = Math.min( maxCardWidth1Across / BigCard.width, maxCardHeight2Down / BigCard.height ),
            bestFitIsAcross = ( scale2Across >= scale2Down ),
            bestScale = Math.min( 1.0, bestFitIsAcross ? scale2Across : scale2Down );
        return {
            is2Across: bestFitIsAcross,
            scale: bestScale,
            width: BigCard.width * bestScale,
            height: BigCard.height * bestScale,
            corner: BigCard.corner,
            borderX: borderX,
            borderY: borderY
        };
    }

    /** create or update the position of the two big card areas */
    arrangeBigCards() {
        let Dyn = this.Dyn,
            fit = this._getBigCardScale();

        // add or reposition card 0
        let bcRect = new Dyn.mathUtil.Rect(
                this.bounds.centerX()-(fit.is2Across?(fit.width + fit.borderX/2):(fit.width/2)),
                this.bounds.centerY()-(fit.is2Across?(fit.height/2):(fit.height + fit.borderY/2)),
                fit.width, fit.height ),
            outline = this.bigCards[0].outline || this.addBigCardOutline( 0, bcRect, fit.corner );
        this.moveBigCardOutline( outline, bcRect );

        // add or reposition card 1
        if( fit.is2Across ) {
            bcRect.x += fit.width + fit.borderX;
        } else {
            bcRect.y += fit.height + fit.borderY;
        }
        outline = this.bigCards[1].outline || this.addBigCardOutline( 1, bcRect, fit.corner );
        this.moveBigCardOutline( outline, bcRect );
    }

    /** add the visible outline to the given big card */
    addBigCardOutline( bigCardIndex, rect /*, corner*/  ) {
        const outline = this.container.paper.foreignObject('', rect.x, rect.y, rect.width, rect.height)
            .data("index", bigCardIndex );
        const body = outline.node.firstChild;
        body.style.height = '100%';
        const container = document.createElement('div');
        container.classList.add('tde-fill');
        body.appendChild(container);
        // Render using react
        const root = ReactDOM.createRoot(container);
        root.render(<BigCardOutline Dyn={this.Dyn} />);
        this.bigCards[bigCardIndex] = new BigCard(outline, root);
        return outline;
    }

    /** add or update a Big Card Case View */
    addBigCardCaseView(bigCardIndex, caseIndex) {
        let Dyn = this.Dyn,
            bigCard = this.bigCards[bigCardIndex],
            smallCardIndex = this.container.home.caseIndexToSmallCardIndex( caseIndex );
        Dyn.assertArrayIndex( this.bigCards, bigCardIndex );
        Dyn.assert( bigCard.caseIndices.includes( caseIndex ), "case should have been added at start of animation", caseIndex );
        bigCard.htmlRoot.render(<BigCardView onClose={() => this.removeCaseFromBigCard(bigCardIndex)}
                                             Dyn={this.Dyn} caseIndex={caseIndex} smallCardIndex={ smallCardIndex }/>);
    }

    moveBigCardOutline( outline, bcRect ) {
        outline.attr({ x:bcRect.x, y:bcRect.y, width:bcRect.width, height:bcRect.height });
        return outline; // for chaining
    }

    highlightBigCard( onOff, bigCardIndex, caseIndex ) {
        this.Dyn.assertValidArray( this.bigCards, bigCardIndex );
        let bigCard = this.bigCards[bigCardIndex];
        if( ! bigCard.cardHasCases()) {
            // Highlight only if the big card is empty
            bigCard.htmlRoot.render(<BigCardOutline Dyn={this.Dyn} highlight={onOff} />);
        }
    }

    /** remove and animate home any previously dropped cases on this big card */
    dropRemovePreviousCases( bigCard ) {
        let anyRemoved = false;
        while( bigCard.cardHasCases()) {
            // return previous cases, if any, to home
            let removedCaseIndex = bigCard.removeCase();
            //console.log( "drop remove previous case", removedCaseIndex );
            let smallCardIndex = this.container.home.caseIndexToSmallCardIndex( removedCaseIndex );
            if( smallCardIndex >= 0 ) { // make sure it was not filtered out
                this.container.home.animateCardToHome( smallCardIndex, this.container.fastAnimation() );
            }
            anyRemoved = true;
        }
        return anyRemoved;
    }

    /**
     * handle start of drop animation
     * NOTE: because animation is slow, multiple cases can be dropped while the
     * first case animation has not completed; here we add and remove cases in a queue for each card,
     * so cases are added and moved in, or removed and moved out, in queue order.
     */
    dropAnimationStart( dragResult ) {
        let Dyn = this.Dyn,
            bigCard = this.bigCards[dragResult.bigCardIndex];
        Dyn.assertArrayIndex( this.bigCards, dragResult.bigCardIndex );

        // remove previous cases from big card, if any
        if( this.dropRemovePreviousCases( bigCard, dragResult.bigCardIndex )) {
            this._updateOneCard( bigCard, dragResult.bigCardIndex );
        }
        // add the new case
        bigCard.addCase( dragResult.caseIndex );
        this.container.setState( this.getBigCardState());
    }

    /** handle end of animation after drop of small card onto big card */
    dropAnimationEnd( dragResult ) {
        let bigCard = this.bigCards[dragResult.bigCardIndex];
        // if dropped small card was not already removed (because of multiple clicks or drags during animation)
        // then finish changing the visible state of the Big Card.
        if( bigCard.caseIndices.includes( dragResult.caseIndex )) {
            this.Dyn.assert( this.container.state.plot === "bigCards");
            this.container.home.hideCardUnderBigCard( dragResult.smallCardIndex );
            this.addBigCardCaseView( dragResult.bigCardIndex, dragResult.caseIndex );
            this.Dyn.toolTip.hide(); // fix bug where tooltip persists on firefox after drop
        }
    }

    /** change a big card from big card case view to an outline, and return the small card home. */
    removeCaseFromBigCard( bigCardIndex ) {
        let bigCard = this.bigCards[bigCardIndex];
        this.Dyn.assert( bigCard.cardHasCases());
        let removedCaseIndex = bigCard.removeCase();

        // animate small card home; show the case below, or card outline if no cases
        let smallCardIndex = this.container.home.caseIndexToSmallCardIndex( removedCaseIndex );
        if( smallCardIndex >= 0 ) { // make sure it was not filtered out
            // move small card to current big card position, then animate home
            let bigCardBBox = new this.Dyn.mathUtil.Rect( bigCard.outline.getBBox() );
            this.container.home.hideCardUnderBigCard( smallCardIndex, bigCardBBox );
            this.container.home.animateCardToHome( smallCardIndex );
        }
        this._updateOneCard( bigCard, bigCardIndex );
        this.container.setState( this.getBigCardState());
    }

    /** remove the small card from any big card that is holding it, e.g. if filtering was changed */
    removeCard( smallCard ) {
        this.bigCards.find( (bigCard, bigCardIndex) => {
            if( bigCard.hasCaseIndex( smallCard.caseIndex )) {
                this.removeCaseFromBigCard( bigCardIndex );
            }
        });
    }

    /** Reset : close all visible big cards, show outlines again. */
    resetBigCards() {
        this.bigCards.forEach((bigCard, bigCardIndex) => {
            while( bigCard.cardHasCases() ) {
                bigCard.removeCase();
            }
            this._updateOneCard( bigCard, bigCardIndex );
        });
        this.container.setState( this.getBigCardState());
    }

    /** restore big card state from state data returned by getBigCardState() */
    restoreBigCards( data ) {
        this.bigCards.forEach((bigCard, bigCardIndex) => {
            while( bigCard.cardHasCases() ) {
                bigCard.removeCase();
            }
            let newCases = data?.cases?.[bigCardIndex] || [];
            newCases.forEach(( caseIndex /*,i*/ ) => {
                bigCard.addCase( caseIndex );
                let smallCardIndex = this.container.home.caseIndexToSmallCardIndex( caseIndex );
                if( smallCardIndex >= 0 ) { // make sure it was not filtered out
                    this.container.home.hideCardUnderBigCard( smallCardIndex );
                }
            });
            this._updateOneCard( bigCard, bigCardIndex );
        });
        this.container.setState( this.getBigCardState());
    }

    /**
     * Close and hide the visible big cards,
     * without changing the location of the small cards dropped onto them,
     * in preparation for moving the cards to an OrderBy or GroupBy plot.
     */
    hideBigCardsAndOutline() {
        this.bigCards.forEach((bigCard) => {
            bigCard.outline.hide();
            while( bigCard.cardHasCases() ) {
                bigCard.removeCase();
            }
        });
    }

    /** card dropped and open in big card area? */
    isCardInBigCardArea( caseIndex ) {
        return Boolean(this.bigCards.find((bc)=>bc.hasCaseIndex(caseIndex)));
    }

    /** is the small card (being dragged) overlapping and droppable on a big card outline? */
    isCardOverlappingBigCardArea( smallCardBBox, caseIndex ) {
        let Dyn = this.Dyn,
            bbox,
            bigCardIndex = this.bigCards.findIndex((bc) => {
                bbox = new Dyn.mathUtil.Rect( bc.outline.getBBox());
                return bc.cardIsVisible( Dyn )
                    && (! bc.hasCaseIndex( caseIndex ))
                    && smallCardBBox.isOverlappingRect( bbox );
            });
        return (bigCardIndex >=0) ? { bigCardIndex:bigCardIndex, bbox:bbox }: null;
    }

    /** is the small card (being dragged), in the big card view? If not fully in the view, return
     * a new location so that it still draggable (e.g. not mostly outside the view and hard to drag again */
    getDragConstraintforBigCardView( cardBBox ) {
        if( this.bounds.isOverlappingRect( cardBBox )) {

        }
        return null;
    }

}


